/**
 * Command Palette (Phase 12) — ⌘K
 *
 * Search : Patient · Invoice · Doctor · Medication · ICD-10 · Module
 * Navigation rapide entre les 5 centres.
 */
import { useEffect, useState, useMemo } from "react";
import { useRouter } from "@tanstack/react-router";
import { Search, Activity, Stethoscope, Pill, FlaskConical, ScanLine, Shield, Brain, Server, Users, FileText, Command, X, Bed, Truck } from "lucide-react";
import { DOMAINS, type Domain } from "@/lib/design-system";

interface CommandItem {
  id: string;
  label: string;
  sublabel?: string;
  domain: Domain;
  href: string;
  icon: any;
  category: "navigation" | "patient" | "module" | "recent";
}

const COMMANDS: CommandItem[] = [
  // === NAVIGATION (6 centres) ===
  { id: "nav-mission", label: "Mission Control", sublabel: "Vue exécutive", domain: "neutral", href: "/cockpits", icon: Activity, category: "navigation" },
  { id: "nav-clinical", label: "Clinical Operations Center", sublabel: "Patients · Labo · Imagerie · Pharma", domain: "clinical", href: "/admin/administrateur", icon: Stethoscope, category: "navigation" },
  { id: "nav-operations", label: "Operations Center", sublabel: "Occupancy · Beds · Staffing · Inventory", domain: "operations", href: "/operations", icon: Bed, category: "navigation" },
  { id: "nav-soc", label: "Security Operations Center", sublabel: "Live monitoring", domain: "security", href: "/soc", icon: Shield, category: "navigation" },
  { id: "nav-governance", label: "Platform Governance Center", sublabel: "IAM · Compliance · Vault", domain: "governance", href: "/admin/siem", icon: Server, category: "navigation" },
  { id: "nav-ai", label: "AI Center", sublabel: "ICD-10 · Drug advisor · Notes", domain: "ai", href: "/ai/icd10-advisor", icon: Brain, category: "navigation" },

  // === MODULES (Clinical) ===
  { id: "mod-patients", label: "Patients", sublabel: "Dossiers + timeline", domain: "clinical", href: "/patients", icon: Users, category: "module" },
  { id: "mod-consultation", label: "Consultation", sublabel: "Notes + IA ICD-10", domain: "clinical", href: "/consultation", icon: Stethoscope, category: "module" },
  { id: "mod-lab", label: "Labo Résultats", sublabel: "LOINC + validation", domain: "operations", href: "/admin/administrateur/lab/resultats", icon: FlaskConical, category: "module" },
  { id: "mod-imaging", label: "Imagerie PACS", sublabel: "DICOM worklist", domain: "operations", href: "/admin/administrateur/imaging/pacs", icon: ScanLine, category: "module" },
  { id: "mod-pharma", label: "Pharmacie Stocks", sublabel: "Seuils + contrôlés", domain: "operations", href: "/admin/administrateur/pharma/stocks", icon: Pill, category: "module" },
  { id: "mod-billing", label: "Facturation", sublabel: "CNSS + claims", domain: "governance", href: "/billing", icon: FileText, category: "module" },

  // === MODULES (Security) ===
  { id: "mod-audit", label: "Audit Feed", sublabel: "Hash chain integrity", domain: "security", href: "/admin/siem", icon: Shield, category: "module" },
  { id: "mod-iomt", label: "IoMT Observations", sublabel: "Live monitoring", domain: "operations", href: "/admin/administrateur/integration/observations", icon: Activity, category: "module" },
  { id: "mod-alerts", label: "Notification Center", sublabel: "Alertes globales", domain: "security", href: "/admin/siem/notifications", icon: Activity, category: "module" },

  // === MODULES (AI) ===
  { id: "mod-icd10", label: "ICD-10 Advisor", sublabel: "Recherche sémantique", domain: "ai", href: "/ai/icd10-advisor", icon: Brain, category: "module" },
  { id: "mod-drugs", label: "Drug Interactions", sublabel: "Vérifier prescription", domain: "ai", href: "/ai/drug-interactions", icon: Pill, category: "module" },
  { id: "mod-notes", label: "Clinical Notes AI", sublabel: "Générer notes SOAP", domain: "ai", href: "/ai/clinical-notes", icon: FileText, category: "module" },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const router = useRouter();

  // ⌘K / Ctrl+K handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // LocalStorage: recent items
  const [recent, setRecent] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("recent_commands") || "[]"); } catch { return []; }
  });

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    let items = COMMANDS;
    if (q) {
      items = items.filter((c) =>
        c.label.toLowerCase().includes(q) ||
        c.sublabel?.toLowerCase().includes(q)
      );
    }
    return items;
  }, [query]);

  const select = (item: CommandItem) => {
    // Track recent
    const next = [item.id, ...recent.filter((r) => r !== item.id)].slice(0, 5);
    setRecent(next);
    try { localStorage.setItem("recent_commands", JSON.stringify(next)); } catch {}

    router.navigate({ to: item.href as any });
    setOpen(false);
    setQuery("");
  };

  if (!open) return null;

  const recents = recent.map((id) => COMMANDS.find((c) => c.id === id)).filter(Boolean) as CommandItem[];

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4" onClick={() => setOpen(false)}>
      <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un patient, un module, un centre…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-500"
          />
          <kbd className="text-[10px] bg-slate-800 px-1.5 py-0.5 rounded text-slate-400 flex items-center gap-1">
            <Command className="h-2.5 w-2.5" />K
          </kbd>
          <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-300">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {recents.length > 0 && !query && (
            <>
              <SectionLabel>Récents</SectionLabel>
              {recents.map((c) => <CommandRow key={c.id} item={c} onClick={() => select(c)} />)}
            </>
          )}

          {query && filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              Aucun résultat pour "{query}"
            </div>
          )}

          {!query && (
            <>
              <SectionLabel>Navigation · 5 centres</SectionLabel>
              {filtered.filter((c) => c.category === "navigation").map((c) => <CommandRow key={c.id} item={c} onClick={() => select(c)} />)}
              <SectionLabel>Modules · accès rapide</SectionLabel>
              {filtered.filter((c) => c.category === "module").slice(0, 9).map((c) => <CommandRow key={c.id} item={c} onClick={() => select(c)} />)}
            </>
          )}

          {query && filtered.length > 0 && (
            <>
              <SectionLabel>{filtered.length} résultat{filtered.length > 1 ? "s" : ""}</SectionLabel>
              {filtered.map((c) => <CommandRow key={c.id} item={c} onClick={() => select(c)} />)}
            </>
          )}
        </div>

        <div className="border-t border-slate-800 px-4 py-2 flex items-center gap-3 text-[10px] text-slate-500">
          <span><kbd className="bg-slate-800 px-1 rounded">↑↓</kbd> naviguer</span>
          <span><kbd className="bg-slate-800 px-1 rounded">↵</kbd> ouvrir</span>
          <span><kbd className="bg-slate-800 px-1 rounded">esc</kbd> fermer</span>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: any }) {
  return <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{children}</div>;
}

function CommandRow({ item, onClick }: { item: CommandItem; onClick: () => void }) {
  const d = DOMAINS[item.domain];
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800/60 transition text-left"
    >
      <div className={`h-8 w-8 rounded-lg ${d.bgClass} ${d.textClass} flex items-center justify-center shrink-0`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{item.label}</div>
        {item.sublabel && <div className="text-xs text-slate-500 truncate">{item.sublabel}</div>}
      </div>
      <Badge variant="outline" className={`text-[10px] ${d.textClass}`}>{d.short}</Badge>
    </button>
  );
}

// Add Badge import
import { Badge } from "@/components/ui/badge";